import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGraphEditorStore } from '@/graph-store';
import { useToast } from '@/hooks/useToast';
import type { OperationNodeSpec } from '@/graph/domain/types';
import { analyzeGroupSubgraph } from '@/graph/domain/groupTopology';

/**
 * Helper to generate human-readable error message from topology analysis
 */
function getTopologyErrorMessage(
  analysis: ReturnType<typeof analyzeGroupSubgraph>,
  operationNodes?: Record<string, any>,
): string {
  if (analysis.ok) return '';

  /**
   * Convert step IDs to user-friendly step numbers/names
   */
  const formatStepId = (stepId: string): string => {
    if (!operationNodes?.[stepId]) return `Step (unknown)`;
    const step = operationNodes[stepId];
    return step.displayName || `Step ${stepId.slice(0, 8)}`;
  };

  switch (analysis.reason) {
    case 'disconnected':
      return 'Selected steps are not connected. Please select steps that form a single connected chain.';
    case 'gap':
      const missingSteps = (analysis.missingStepIds || []).map(formatStepId).join(', ');
      return `Selected steps have gaps. Missing: ${missingSteps || 'unknown'}.`;
    case 'duplicate':
      return 'Selected steps contain duplicates.';
    case 'missing-step':
      const missingStep = analysis.missingStepIds?.[0];
      return `Step not found: ${missingStep ? formatStepId(missingStep) : 'unknown'}.`;
    case 'already-grouped':
      return 'One or more steps already belong to another group.';
    default:
      return 'Invalid group selection.';
  }
}

export interface AddGroupModalProps {
  preselectedStepIds?: string[];
  onClose: () => void;
}

export function AddGroupModal({ preselectedStepIds, onClose }: AddGroupModalProps) {
  const graphData = useGraphEditorStore((s) => s.graphData);
  const addGroup = useGraphEditorStore((s) => s.addGroup);
  const { showError } = useToast();

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [enableCycle, setEnableCycle] = useState(false);
  const [cycleCount, setCycleCount] = useState(2);
  const validatedRef = useRef(false);

  // All operations sorted by creationOrder
  const sortedOps = useMemo(() => {
    return Object.values(graphData.operationNodes)
      .filter((op): op is OperationNodeSpec & { id: string } => !!op.id)
      .sort((a, b) => (a.creationOrder ?? 0) - (b.creationOrder ?? 0));
  }, [graphData.operationNodes]);

  // If preselected, use those; otherwise let user pick range
  const [fromId, setFromId] = useState<string>(preselectedStepIds?.[0] ?? '');
  const [toId, setToId] = useState<string>(
    preselectedStepIds?.[preselectedStepIds.length - 1] ?? '',
  );

  const selectedStepIds = useMemo(() => {
    if (preselectedStepIds?.length) return preselectedStepIds;
    if (!fromId || !toId) return [];
    const fromIdx = sortedOps.findIndex((op) => op.id === fromId);
    const toIdx = sortedOps.findIndex((op) => op.id === toId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx > toIdx) return [];
    return sortedOps.slice(fromIdx, toIdx + 1).map((op) => op.id!);
  }, [preselectedStepIds, fromId, toId, sortedOps]);

  const conflictWarnings = useMemo(() => {
    return selectedStepIds.filter((id) =>
      Object.values(graphData.groups).some((g) => g.stepIds.includes(id)),
    );
  }, [selectedStepIds, graphData.groups]);

  // Validate group topology for selected steps
  const topologyValidation = useMemo(() => {
    if (selectedStepIds.length === 0) return { ok: true as const };
    return analyzeGroupSubgraph(selectedStepIds, graphData);
  }, [selectedStepIds, graphData]);

  const isValid = topologyValidation.ok && !conflictWarnings.length;

  // Validate preselected steps on modal open (only once)
  useEffect(() => {
    if (!preselectedStepIds?.length) return;
    if (validatedRef.current) return;

    validatedRef.current = true;

    if (!topologyValidation.ok) {
      const errorMsg = getTopologyErrorMessage(topologyValidation, graphData.operationNodes);
      showError(errorMsg);
      onClose();
    } else if (conflictWarnings.length > 0) {
      const errorMsg = `Warning: ${conflictWarnings.length} step(s) already belong to another group.`;
      showError(errorMsg);
      onClose();
    }
  }, []);

  const handleSave = () => {
    if (!name.trim()) return;
    if (selectedStepIds.length === 0) return;
    try {
      const cycleRanges = enableCycle && cycleCount >= 2
        ? [{ id: crypto.randomUUID(), startIndex: 0, endIndex: selectedStepIds.length - 1, iterationCount: cycleCount }]
        : [];

      const groupSpec = {
        label: name.trim(),
        stepIds: selectedStepIds,
        cycleRanges,
        notes: notes || undefined,
      };

      const groupId = addGroup(groupSpec);

      // Verify group was actually added to store
      const updatedGroups = useGraphEditorStore.getState().graphData.groups;
      const createdGroup = updatedGroups[groupId];

      if (!createdGroup) {
        const errorMsg = 'Group was created but failed to persist in store. Please try again.';
        showError(errorMsg);
        onClose();
        return;
      }

      onClose();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      showError(errorMsg);
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Add Group
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            required
            size="small"
            fullWidth
            placeholder="Enter group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />

          {!preselectedStepIds?.length && (
            <>
              <Typography variant="subtitle2">Steps (pick a contiguous range)</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  select
                  label="From step"
                  size="small"
                  fullWidth
                  value={fromId}
                  onChange={(e) => setFromId(e.target.value)}
                >
                  {sortedOps.map((op) => (
                    <MenuItem key={op.id} value={op.id!}>
                      {op.displayName}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="To step"
                  size="small"
                  fullWidth
                  value={toId}
                  onChange={(e) => setToId(e.target.value)}
                >
                  {sortedOps.map((op) => (
                    <MenuItem key={op.id} value={op.id!}>
                      {op.displayName}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </>
          )}

          {selectedStepIds.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Steps in this range ({selectedStepIds.length}):
              </Typography>
              {selectedStepIds.map((id) => (
                <Typography key={id} variant="body2">
                  &bull; {graphData.operationNodes[id]?.displayName ?? id}
                </Typography>
              ))}
            </Box>
          )}

          {conflictWarnings.length > 0 && (
            <Typography variant="body2" color="warning.main">
              Warning: {conflictWarnings.length} step(s) already belong to another group.
            </Typography>
          )}

          <TextField
            label="Notes"
            multiline
            rows={2}
            size="small"
            fullWidth
            placeholder="Enter Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <Box>
            {/* <FormControlLabel
              control={
                <Checkbox
                  checked={enableCycle}
                  onChange={(e) => setEnableCycle(e.target.checked)}
                />
              }
              label="Enable cycling (repeat steps)"
            /> */}
            {enableCycle && (
              <TextField
                label="Number of cycles"
                type="number"
                size="small"
                sx={{ ml: 4, width: 160 }}
                value={cycleCount}
                onChange={(e) => setCycleCount(Math.max(2, parseInt(e.target.value, 10) || 2))}
                slotProps={{ htmlInput: { min: 2 } }}
              />
            )}
          </Box>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || selectedStepIds.length === 0 || !isValid}
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
